import { Icon } from "@blueprintjs/core"
import { useState } from "react"
import React from "react"
import { useFormContext } from "react-hook-form"

import styles from "./PhaseRoleSection.module.css"

import { formatCurrency } from "~/helpers/CurrencyHelper"
import DurationHelper from "~/helpers/DurationHelper"
import { formatMinutesToDays } from "~/helpers/budgetHelper"

import PhaseIcon from "~/common/PhaseIcon"

import BudgetTableHeader from "../BudgetTableHeader"
import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import { ProjectBudgetFormData } from "../formTypes"
import { calculatePhaseRoleTotalMinutes } from "../projectBudgetHelpers"
import { BudgetedPhaseAndRole, BudgetedRole, RateCard, PhaseId } from "../types"

import { calculatePhaseRoleBudget } from "~/helpers/project-helpers"
import PhaseBudgetItemRow from "./PhaseBudgetItemRow"
import PhaseRoleAdder from "./PhaseRoleAdder"

type Props = {
  phase: {
    id: PhaseId
    name: string
    color: string
  }
  phaseRoles: BudgetedPhaseAndRole[]
  unallocatedRoles: readonly BudgetedRole[]
  rateCard: RateCard
  defaultFullTimeMinutes: number
  onAddRole: (phaseId: PhaseId, roleId: number) => void
  onShowAllRoles: (phaseId: PhaseId) => void
}

const PhaseRoleSection: React.FC<Props> = (props) => {
  const {
    phase,
    phaseRoles,
    unallocatedRoles,
    rateCard,
    defaultFullTimeMinutes,
    onAddRole,
    onShowAllRoles,
  } = props

  const [isExpanded, setIsExpanded] = useState(false)
  const { showFinancialColumns, showDayView, allRoles } =
    useProjectBudgetFormContext()
  const { watch } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  const roleMap = new Map(allRoles.map((role) => [role.id, role]))
  const totalMinutes = calculatePhaseRoleTotalMinutes(phaseRoles)
  const totalPhaseBudget = calculatePhaseRoleBudget(
    phaseRoles,
    rateCard,
    formData.projectRates,
  )

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  const dataTestPhaseName = phase.name === "No Phase" ? "no-phase" : phase.name

  return (
    <div>
      <div
        className={styles.phaseHeader}
        data-test={`phase-header-${dataTestPhaseName}`}
        onClick={handleToggle}
      >
        <div className={styles.phaseHeaderLeft}>
          <div className={styles.chevron}>
            <Icon
              icon={isExpanded ? "chevron-down" : "chevron-right"}
              size={16}
            />
          </div>
          <PhaseIcon size={20} color={phase.color} />
          <span className={styles.phaseName}>{phase.name}</span>
        </div>
        <div className={styles.phaseHeaderRight}>
          <div className={styles.totalHours}>
            {showDayView
              ? formatMinutesToDays(totalMinutes, defaultFullTimeMinutes)
              : DurationHelper.formatMinutesToTime(totalMinutes)}
          </div>
          {showFinancialColumns && (
            <div className={styles.totalBudget}>
              {formatCurrency(totalPhaseBudget)}
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div
          className={styles.phaseContent}
          data-test={`phase-content-${dataTestPhaseName}`}
        >
          {phaseRoles.length > 0 && (
            <BudgetTableHeader
              budgetColumnLabel="Role Budget"
              rowClassName={styles.subHeading}
            />
          )}

          <div className={styles.roleTable}>
            {phaseRoles.map((phaseRole) => {
              const role = roleMap.get(phaseRole.roleId)
              if (!role) return null

              return (
                <PhaseBudgetItemRow
                  key={`${dataTestPhaseName}-${phaseRole.roleId}`}
                  phaseId={phase.id}
                  role={role}
                  phaseRole={phaseRole}
                  rateCard={rateCard}
                  defaultFullTimeMinutes={defaultFullTimeMinutes}
                  canDelete={true}
                  deleteTooltip={`Remove "${role.name}" from this phase`}
                />
              )
            })}
          </div>

          {/* Only show the role adder if there are roles that have not already been added to this phase */}
          {unallocatedRoles.length > 0 && (
            <div>
              <PhaseRoleAdder
                phaseId={phase.id}
                phaseName={phase.name}
                unallocatedRoles={unallocatedRoles}
                onAddRole={onAddRole}
                onShowAllRoles={onShowAllRoles}
                className={styles.roleAdder}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PhaseRoleSection
